import { Dictionary } from 'lodash';
import { User } from 'api/models';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import { teamSummaryTooltip } from 'components/organisms/resource-management/scheduler-config/templates/team-summary-template';
import { useTranslate } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { SchedulerEventType } from '../../event-types';
import { allocationTooltip } from './allocation-template';
import { companySummaryTooltip } from './company-summary-template';
import { demandRoleTooltip } from './demand-role-template';
import { projectPhaseTooltip } from './project-phase-template';
import { projectTimelineTooltip } from './project-timeline-template';
import { timeoffTooltip } from './timeoff-template';
import { userSummaryTooltip } from './user-summary-template';

export const tooltipForEvent = (
  event: AllocationEventModel,
  translate: ReturnType<typeof useTranslate>,
  extra: {
    resourceName?: string;
    softAllocationsEnabled?: boolean;
    userLookup?: Dictionary<User>;
  } = {},
  isHoursPerWeek?: boolean,
): string | null => {
  switch (event.get('type')) {
    case SchedulerEventType.ProjectPhase:
      return projectPhaseTooltip({
        phaseName: event.get('phaseName'),
        startDate: event.startDate as Date,
        endDate: event.endDate as Date,
      });
    case SchedulerEventType.Allocation:
      return allocationTooltip(
        {
          startDate: event.startDate as Date,
          endDate: event.endDate as Date,
          projectPhase: event.get('projectPhase'),
          billable: event.get('billable'),
          totalMinutes: event.get('totalMinutes'),
          minutes: event.get('minutes'),
        },
        translate,
        isHoursPerWeek,
      );
    case SchedulerEventType.ProjectTimeline:
      return projectTimelineTooltip(
        {
          startDate: event.startDate as Date,
          endDate: event.endDate as Date,
        },
        translate,
      );
    case SchedulerEventType.UserAllocationSummary:
      return userSummaryTooltip(
        {
          allocatedMinutes: event.get('allocatedMinutes'),
          name: extra.resourceName,
          startDate: event.startDate as Date,
          endDate: event.endDate as Date,
          capacity: event.get('capacity'),
          forViewPreset: event.get('forViewPreset'),
          softAllocationsEnabled: extra.softAllocationsEnabled,
        },
        translate,
      );
    case SchedulerEventType.TeamAllocationSummary:
      return teamSummaryTooltip(
        {
          allocatedMinutes: event.get('allocatedMinutes'),
          name: extra.resourceName,
          startDate: event.startDate as Date,
          endDate: event.endDate as Date,
          forViewPreset: event.get('forViewPreset'),
        },
        translate,
      );
    case SchedulerEventType.CompanyAllocationSummary:
      return companySummaryTooltip(
        {
          allocatedMinutes: event.get('allocatedMinutes'),
          name: extra.resourceName,
          startDate: event.startDate as Date,
          endDate: event.endDate as Date,
          forViewPreset: event.get('forViewPreset'),
        },
        translate,
      );
    case SchedulerEventType.DemandRole:
      return demandRoleTooltip(
        {
          id: event.id,
          roleName: extra.resourceName ?? '',
          roleId: event.get('roleId'),
          usersAllocatedMinutes: event.get('usersAllocatedMinutes'),
          teamsAllocatedMinutes: event.get('teamsAllocatedMinutes'),
          capacity: event.get('capacity'),
        },
        translate,
      );
    case SchedulerEventType.UserTimeOff: {
      const timeOff = event.get('timeOff');
      const userId = event.get('userId');
      const user = extra.userLookup?.[userId];

      return timeoffTooltip({
        userName: fullName({
          firstName: user?.firstName,
          lastName: user?.lastName,
        }),
        timeOff,
      });
    }
    default:
      return null;
  }
};
